// iter-30 Phase 1 — Super Admin LLM Routing Map.
// Displays the 7 surfaces × current platform-default (model, provider) and
// lets the admin swap any of them. No "Boost mode" yet — that's Phase 2.
//
// Cascade: silo → creator → platform → fallback. This panel only edits the
// platform layer. Creator overrides live on the Creator Dashboard; silo
// overrides live in SiloManage Settings.

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowsCounterClockwise, Crosshair, Lightning, Brain, Wrench, ChatTeardropText, Export, Robot, Funnel, SlidersHorizontal, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import InfoTip from "@/components/InfoTip";
import { MODEL_GLOSSARY, modelTooltipBody } from "@/lib/modelGlossary";
import ModelOutputPanel from "@/components/ModelOutputPanel";

// Surface → icon mapping. Keeps the table visually scannable.
const SURFACE_ICONS = {
  public_ask: Crosshair,
  compress: Lightning,
  tone_architect: ChatTeardropText,
  qa_probe: Brain,
  export_summary: Export,
  workbench_run: Wrench,
  ai_assistant: Robot,
  refinery: Funnel,
};

const TIER_BADGE = {
  "user-facing": { bg: "bg-[#FCEDE6]", fg: "text-[#A85E4A]", label: "User-facing" },
  authoring: { bg: "bg-[#E8F0E9]", fg: "text-[#3A4A3E]", label: "Authoring" },
  background: { bg: "bg-[#F0F1EE]", fg: "text-[#6B6A68]", label: "Background" },
};

export default function LLMRoutingPanel() {
  const [data, setData] = useState(null);
  const [registry, setRegistry] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState({});  // surfaceId -> {model, provider}
  // iter-40 — per-surface [Model & output length] tune modal state.
  const [tuneSurface, setTuneSurface] = useState(null);  // the surface row currently open in the modal
  const [tuneValue, setTuneValue] = useState(null);      // live form values inside the modal
  const [tuneSnapshot, setTuneSnapshot] = useState(null); // remembers the persisted values for Reset

  const reload = async () => {
    try {
      const [r, reg] = await Promise.all([
        api.get("/admin/llm-routing"),
        api.get("/models/registry"),
      ]);
      setData(r.data);
      setRegistry(reg.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load LLM routing config", {
        id: "admin-llm-routing-load-failed",
      });
    }
  };
  useEffect(() => { reload(); }, []);

  const allModelChoices = useMemo(() => {
    if (!registry?.providers) return [];
    const list = [];
    for (const p of registry.providers) {
      const pricing = p.pricing_per_1m || {};
      for (const m of (p.models || [])) {
        const px = pricing[m] || {};
        list.push({
          provider: p.provider, model: m,
          label: `${p.label} · ${m}`,
          inPrice: px.in,
          outPrice: px.out,
        });
      }
    }
    return list;
  }, [registry]);

  const priceFor = (model) => allModelChoices.find((c) => c.model === model);

  const currentFor = (sid) => {
    if (pending[sid]) return pending[sid];
    return data?.routes?.[sid] || null;
  };

  const isDirty = (sid) => {
    const a = pending[sid];
    if (!a) return false;
    const b = data?.routes?.[sid];
    return !b || a.model !== b.model || a.provider !== b.provider;
  };

  const onPick = (sid, value) => {
    if (!value) return;
    const [provider, model] = value.split("|");
    setPending((p) => ({ ...p, [sid]: { model, provider } }));
  };

  const saveSurface = async (sid) => {
    const next = pending[sid];
    if (!next) return;
    setBusy(true);
    try {
      await api.patch("/admin/llm-routing", { routes: { [sid]: next } });
      await reload();
      setPending((p) => { const { [sid]: _drop, ...rest } = p; return rest; });
      toast.success(`${sid} routed to ${next.model}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const saveAll = async () => {
    const dirty = Object.entries(pending).filter(([sid]) => isDirty(sid));
    if (dirty.length === 0) { toast.info("No changes to save."); return; }
    setBusy(true);
    try {
      await api.patch("/admin/llm-routing", { routes: Object.fromEntries(dirty) });
      await reload();
      setPending({});
      toast.success(`Saved ${dirty.length} surface${dirty.length === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const resetAllModels = async () => {
    if (!window.confirm("Reset every surface's LLM (models + sliders) back to your Site defaults? If you've never set Site defaults, this uses the factory defaults.")) return;
    setBusy(true);
    try {
      await api.post("/admin/llm-routing/reset-models");
      await reload();
      setPending({});
      toast.success("All LLMs reset to Site defaults.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const resetAllSliders = async () => {
    if (!window.confirm("Reset every surface's sliders (output length, memory, bridge) back to your Site defaults? Model choices stay as-is.")) return;
    setBusy(true);
    try {
      await api.post("/admin/llm-routing/reset-sliders");
      await reload();
      toast.success("All sliders reset to Site defaults.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  // Phase 4 (Jun 2026, Randy) — re-baseline + site overhaul.
  const setAsSiteDefault = async () => {
    if (!window.confirm("Save your CURRENT model + slider picks as the Site defaults?\n\nThis clears the red 'overridden' flags (they now compare against this snapshot). It does NOT change any creator or silo.")) return;
    setBusy(true);
    try {
      await api.post("/admin/llm-routing/set-as-default");
      await reload();
      toast.success("Current settings saved as Site defaults.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to set defaults");
    } finally { setBusy(false); }
  };

  const forceAllOntoDefaults = async () => {
    if (!window.confirm("SITE OVERHAUL — force EVERY creator and silo onto the current platform defaults?\n\nThis ERASES all their custom LLM + slider choices. It cannot be undone.")) return;
    if (!window.confirm("Are you absolutely sure? Every creator/silo override will be wiped.")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/llm-routing/force-all");
      await reload();
      toast.success(`Forced ${r.data.creators_cleared} creator(s) + ${r.data.silos_cleared} silo(s) onto current defaults.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Site overhaul failed");
    } finally { setBusy(false); }
  };

  // iter-40 — open the per-surface tune modal. Loads the persisted tune doc
  // from the backend so the four sliders open in their saved positions.
  const openTune = async (surface) => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/llm-routing/tune/${surface.id}`);
      const seed = {
        model_name: r.data.model || surface.default_model || "",
        max_output_tokens: r.data.max_output_tokens ?? 4096,
        default_max_output_tokens: r.data.default_max_output_tokens ?? r.data.max_output_tokens ?? 4096,
        provider: r.data.provider || surface.default_provider || "gemini",
      };
      setTuneSurface(surface);
      setTuneValue(seed);
      setTuneSnapshot(seed);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load tune config.");
    } finally { setBusy(false); }
  };

  const closeTune = () => {
    setTuneSurface(null);
    setTuneValue(null);
    setTuneSnapshot(null);
  };

  const saveTune = async () => {
    if (!tuneSurface || !tuneValue) return;
    setBusy(true);
    try {
      // Resolve provider from the model name when a custom model is picked.
      const provider = (() => {
        if (!tuneValue.model_name) return null;
        if (tuneValue.model_name.startsWith("gemini")) return "gemini";
        if (tuneValue.model_name.startsWith("gpt")) return "openai";
        if (tuneValue.model_name.startsWith("claude")) return "anthropic";
        return tuneValue.provider || "gemini";
      })();
      const payload = {
        max_output_tokens: tuneValue.max_output_tokens,
        default_max_output_tokens: tuneValue.default_max_output_tokens,
      };
      if (tuneValue.model_name) {
        payload.model = tuneValue.model_name;
        payload.provider = provider;
      }
      await api.patch(`/admin/llm-routing/tune/${tuneSurface.id}`, payload);
      toast.success(`${tuneSurface.label} tune saved.`);
      await reload();
      closeTune();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const resetTuneModel = async () => {
    if (!tuneSurface) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/llm-routing/tune/${tuneSurface.id}/reset-model`);
      // Reload the values to pick up the platform default model.
      setTuneValue((v) => ({
        ...v,
        model_name: r.data.model || tuneSurface.default_model || "",
        provider: r.data.provider || tuneSurface.default_provider || "gemini",
      }));
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const resetTuneSliders = async () => {
    if (!tuneSurface) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/llm-routing/tune/${tuneSurface.id}/reset-sliders`);
      const def = r.data.defaults || {};
      setTuneValue((v) => ({
        ...v,
        max_output_tokens: def.max_output_tokens ?? 4096,
        default_max_output_tokens: def.default_max_output_tokens ?? 4096,
      }));
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  if (!data || !registry) {
    return <div className="text-sm text-[#6B6A68] py-8">Loading LLM routing map…</div>;
  }

  const dirtyCount = Object.keys(pending).filter(isDirty).length;

  // Phase 4 — "6/11 1:45p" stamp for the dynamic reset button name.
  const fmtSetAt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    let h = d.getHours();
    const ap = h >= 12 ? "p" : "a";
    h = h % 12 || 12;
    return `${d.getMonth() + 1}/${d.getDate()} ${h}:${String(d.getMinutes()).padStart(2, "0")}${ap}`;
  };
  const baselineFor = (sid) => data?.baseline?.routes?.[sid] || null;
  const setAt = data?.baseline?.set_at;

  return (
    <section className="mt-2" data-testid="llm-routing-panel">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-serif-display text-2xl">LLM Routing Map</h2>
          <p className="text-xs text-[#6B6A68] mt-1 max-w-2xl">
            Per-surface model assignments at the <strong>platform</strong> layer.
            Creators can override on their Dashboard; silos can override per-silo.
            The cascade resolves silo → creator → platform → fallback for every LLM call.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={setAsSiteDefault} disabled={busy} data-testid="llm-routing-set-as-default"
            className="text-xs px-3 py-2 rounded-md border-2 border-[#4A5D4E] text-[#4A5D4E] hover:bg-[#4A5D4E] hover:text-white flex items-center gap-1 font-medium">
            <SlidersHorizontal size={12} weight="duotone" /> Set current settings as Site defaults
          </button>
          <button onClick={resetAllSliders} disabled={busy} data-testid="llm-routing-reset-sliders"
            className="text-xs px-3 py-2 rounded-md border hairline hover:border-[#4A5D4E] flex items-center gap-1 text-[#6B6A68] hover:text-[#4A5D4E]">
            <ArrowsCounterClockwise size={12} /> Reset all sliders to Default
          </button>
          <button onClick={resetAllModels} disabled={busy} data-testid="llm-routing-reset-models"
            className="text-xs px-3 py-2 rounded-md border hairline hover:border-[#C16E5A] flex items-center gap-1 text-[#6B6A68] hover:text-[#C16E5A]">
            <ArrowsCounterClockwise size={12} /> Reset all LLMs to Default{setAt ? ` · ${fmtSetAt(setAt)}` : ""}
          </button>
          <button onClick={forceAllOntoDefaults} disabled={busy} data-testid="llm-routing-force-all"
            className="text-xs px-3 py-2 rounded-md border-2 border-[#C16E5A] text-[#C16E5A] hover:bg-[#C16E5A] hover:text-white flex items-center gap-1">
            <ArrowsCounterClockwise size={12} /> Site overhaul — force all onto defaults
          </button>
          <button onClick={saveAll} disabled={busy || dirtyCount === 0} data-testid="llm-routing-save-all"
            className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50">
            Save {dirtyCount > 0 ? `${dirtyCount} change${dirtyCount === 1 ? "" : "s"}` : "all"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="llm-routing-grid">
        {data.surfaces.map((s) => {
          const cur = currentFor(s.id);
          const Icon = SURFACE_ICONS[s.id] || Robot;
          const tier = TIER_BADGE[s.tier] || TIER_BADGE.background;
          const dirty = isDirty(s.id);
          return (
            <div key={s.id} data-testid={`llm-surface-row-${s.id}`}
              className={`bg-white border hairline rounded-lg p-4 transition-colors relative ${dirty ? "border-[#C16E5A] bg-[#FFF7F1]" : ""}`}>
              {/* iter-40 — top-right "Tune" button. Opens the [Model & output
                  length] modal scoped solely to this surface (its sliders
                  travel with the LLM when later dropped into a shelf). */}
              <button
                type="button"
                onClick={() => openTune(s)}
                disabled={busy}
                data-testid={`llm-surface-tune-${s.id}`}
                title="Tune model + output length for this surface"
                className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded-md border hairline text-[#6B6A68] hover:text-[#4A5D4E] hover:border-[#4A5D4E] flex items-center gap-1 bg-white"
              >
                <SlidersHorizontal size={11} weight="duotone" /> Tune
              </button>
              <div className="flex items-start gap-3 pr-20">
                <div className="w-9 h-9 rounded-md bg-[#F4F2EC] flex items-center justify-center shrink-0">
                  <Icon size={18} weight="duotone" className="text-[#1C1B1A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-sm text-[#1C1B1A]">{s.label}</div>
                    <span className={`text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded ${tier.bg} ${tier.fg}`}>{tier.label}</span>
                  </div>
                  <p className="text-[11px] text-[#6B6A68] mt-1 leading-relaxed">{s.description}</p>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mt-2">
                    Surface id · <span className="font-mono normal-case tracking-normal text-[#1C1B1A]">{s.id}</span>
                  </div>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <select
                  data-testid={`llm-surface-select-${s.id}`}
                  value={cur ? `${cur.provider}|${cur.model}` : ""}
                  onChange={(e) => onPick(s.id, e.target.value)}
                  disabled={busy}
                  className="flex-1 min-w-0 text-xs px-2 py-2 border hairline rounded-md bg-white"
                >
                  {allModelChoices.map((c) => (
                    <option key={`${c.provider}|${c.model}`} value={`${c.provider}|${c.model}`}>{c.label}</option>
                  ))}
                </select>
                <button
                  onClick={() => saveSurface(s.id)}
                  disabled={busy || !dirty}
                  data-testid={`llm-surface-save-${s.id}`}
                  className="text-xs px-3 py-2 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Save
                </button>
              </div>

              <div className="mt-2 text-[10px] text-[#6B6A68] flex items-center gap-2 flex-wrap">
                <span>Site default · <span className="font-mono">{baselineFor(s.id)?.model || s.default_model}</span></span>
                {cur?.model !== (baselineFor(s.id)?.model || s.default_model) && (
                  <span className="text-[#C16E5A]">· overridden</span>
                )}
                {cur?.model && priceFor(cur.model) && (
                  <span className="ml-auto text-[#1C1B1A] font-mono tabular-nums" data-testid={`llm-cost-${s.id}`}>
                    ≈ ${priceFor(cur.model).inPrice?.toFixed(2)} in / ${priceFor(cur.model).outPrice?.toFixed(2)} out per 1M tokens
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-[#F4F2EC] border hairline rounded-lg text-xs text-[#6B6A68] leading-relaxed">
        <strong>Phase 1 scope.</strong> This panel edits the <em>platform</em> layer only.
        Creator-level overrides arrive in the next pass; per-silo overrides remain available
        in <code className="bg-white px-1 py-0.5 rounded font-mono">SiloManage → Settings → Model</code>
        (which now feeds the <code className="bg-white px-1 py-0.5 rounded font-mono">public_ask</code> surface).
        <br />
        <strong>Boost mode</strong> (temporary upgrade with cost guardrails) is the next iter — it sits on top of this cascade as an opt-in, time-boxed per-session escalation.
      </div>

      {/* iter-30 — Model glossary. One row per available model with an
          eyeball tooltip that explains strengths, costs, sweet-spots, and an
          insider tip. Same content also lives in the public FAQ. */}
      <section className="mt-10" data-testid="llm-model-glossary">
        <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-serif-display text-2xl">Model glossary</h3>
            <p className="text-xs text-[#6B6A68] mt-1 max-w-2xl">
              Hover the <span className="inline-flex items-center gap-1"><span className="text-[#4A5D4E]">eye</span></span> next to each model
              for the full breakdown — strengths, pricing, sweet spots, and one insider tip.
              Same reference is mirrored in the public FAQ so creators can read it standalone.
            </p>
          </div>
        </div>
        <div className="bg-white border hairline rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F2EC] text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
              <tr>
                <th className="text-left p-3 font-medium">Model</th>
                <th className="text-left p-3 font-medium">Provider</th>
                <th className="text-right p-3 font-medium">In · Out / 1M</th>
                <th className="text-left p-3 font-medium">Best for</th>
                <th className="text-right p-3 font-medium pr-4">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {Object.entries(MODEL_GLOSSARY).map(([id, m]) => {
                const px = priceFor(id);
                const body = modelTooltipBody(id);
                return (
                  <tr key={id} data-testid={`glossary-row-${id}`} className="hover:bg-[#FDFCF8]">
                    <td className="p-3">
                      <div className="font-medium text-[#1C1B1A]">{m.label}</div>
                      <div className="font-mono text-[10px] text-[#6B6A68]">{id}</div>
                    </td>
                    <td className="p-3 text-[#6B6A68] text-xs">{m.provider}</td>
                    <td className="p-3 text-right tabular-nums text-xs text-[#1C1B1A]">
                      {px ? <>${px.inPrice?.toFixed(2)} · ${px.outPrice?.toFixed(2)}</> : "—"}
                    </td>
                    <td className="p-3 text-[#6B6A68] text-xs max-w-md">{m.sweet_spots.split(",")[0]}</td>
                    <td className="p-3 text-right pr-4">
                      <InfoTip
                        icon="eye"
                        iconSize={18}
                        label={`About ${m.label}`}
                        placement="bottom-left"
                        width={420}
                        testid={`glossary-tip-${id}`}
                      >
                        <div>
                          <div className="font-medium text-[#FDFCF8] text-xs mb-1">{body.title}</div>
                          <div className="text-[#FDFCF8]/80 mb-3 italic">{body.blurb}</div>
                          {body.sections.map((sec, i) => (
                            <div key={i} className="mb-2">
                              <div className="text-[9px] uppercase tracking-[0.2em] text-[#FDFCF8]/60 mb-0.5">
                                {sec.label}
                              </div>
                              <div className="text-[11px] text-[#FDFCF8]/95 leading-relaxed">{sec.text}</div>
                            </div>
                          ))}
                        </div>
                      </InfoTip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-[#6B6A68] mt-2 italic">
          Pricing is public list ($USD per 1M tokens). Caching, fine-tunes, and platform-provider
          key rates may differ — see the <a href="/faq#llm-models" className="underline">full FAQ section</a>.
        </p>
      </section>

      {/* iter-40 — Per-surface [Model & output length] modal. Mounted once at
          the panel level and toggled via openTune/closeTune. State lives in
          the panel so the four sliders maintain their values across user
          edits without remounting. Save flushes to /admin/llm-routing/tune
          (creates llm_surface_tune docs that travel with the LLM into shelves). */}
      {tuneSurface && tuneValue && (
        <div
          className="fixed inset-0 z-[80] bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
          onClick={closeTune}
          data-testid={`tune-modal-${tuneSurface.id}`}
        >
          <div
            className="bg-[#FDFCF8] rounded-lg shadow-2xl max-w-2xl w-full mt-12 mb-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b hairline">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">
                  Per-surface tune · <span className="font-mono normal-case tracking-normal text-[#1C1B1A]">{tuneSurface.id}</span>
                </div>
                <h3 className="font-serif-display text-xl mt-0.5">{tuneSurface.label}</h3>
              </div>
              <button
                type="button"
                onClick={closeTune}
                data-testid={`tune-modal-close-${tuneSurface.id}`}
                className="text-[#6B6A68] hover:text-[#1C1B1A] p-1.5 -m-1.5"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <ModelOutputPanel
                scope={`surface-${tuneSurface.id}`}
                heading="Model & output length"
                subtitle={
                  <>
                    These dials travel with this LLM. They do <strong>not</strong> override the platform routing cascade for other surfaces — they describe how this specific surface ({tuneSurface.label}) behaves whenever it's invoked, including when later dropped into a shelf.
                  </>
                }
                value={tuneValue}
                defaults={tuneSnapshot}
                registry={registry}
                busy={busy}
                onChange={setTuneValue}
                onSave={saveTune}
                onResetModel={resetTuneModel}
                onResetSliders={resetTuneSliders}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
